import { useState, useCallback, useRef, useMemo } from "react";
import { AxiosRequestConfig } from "axios";
import axiosConfig from "../../utils/axiosConfig";
import { baseUrl } from "../../config";
import useFetch from "../../hooks/useFetch";
import {
  FetchFinanceExportDataParams,
  FinancialExportItem,
  FinanceExportRequestData,
  FinanceExportTypeOption,
  FinanceExportCompanyOption,
  FinanceExportSourceOption,
} from "./type";
import { MIME_TYPES } from "../../utils/common";

export const useFinanceExportData = (dashboardId: number) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [tableData, setTableData] = useState<FinancialExportItem[]>([]);
  const [downloadingItems, setDownloadingItems] = useState<Set<number>>(
    new Set()
  );
  const [totalCount, setTotalCount] = useState<number>(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchFinanceExportData = useCallback(
    async (params: FetchFinanceExportDataParams) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const currentController = new AbortController();
      abortControllerRef.current = currentController;

      setLoading(true);
      setError(null);

      try {
        const queryParams = new URLSearchParams({
          page_size: params.page_size.toString(),
          page: params.page.toString(),
          dashboard_id: dashboardId.toString(),
        });

        const response = await axiosConfig({
          method: "GET",
          url: `${baseUrl}/finance-export?${queryParams.toString()}`,
          signal: currentController.signal,
        });

        if (!currentController.signal.aborted) {
          setTableData(response.data.results ?? []);
          setTotalCount(response.data.count ?? 0);
        }
      } catch (err: any) {
        if (
          err.name === "CanceledError" ||
          err.name === "AbortError" ||
          err.code === "ERR_CANCELED" ||
          err.message?.includes("canceled") ||
          err.message?.includes("aborted")
        ) {
          return;
        }

        console.error(err);
        if (!currentController.signal.aborted) {
          setTableData([]);
          setTotalCount(0);
          setError(err as Error);
        }
      } finally {
        if (!currentController.signal.aborted) {
          setLoading(false);
        }
      }
    },
    []
  );

  const fetchTypeOptions = useMemo<AxiosRequestConfig | null>(() => {
    return {
      method: "GET",
      url: `${baseUrl}/finance-export-data-type-list?dashboard_id=${dashboardId}`,
    };
  }, [dashboardId]);

  const { data: typeOptionsResponse, loading: typeOptionsLoading } = useFetch<{
    data: FinanceExportTypeOption[];
  }>(fetchTypeOptions);

  const typeOptions = typeOptionsResponse?.data || [];

  const fetchCompanyOptions = useMemo<AxiosRequestConfig | null>(() => {
    return {
      method: "GET",
      url: `${baseUrl}/finance-export-company-list?dashboard_id=${dashboardId}`,
    };
  }, [dashboardId]);

  const { data: companyOptionsResponse, loading: companyOptionsLoading } =
    useFetch<{ data: { id: number; label: string }[] }>(fetchCompanyOptions);

  const companyOptions: FinanceExportCompanyOption[] = (
    companyOptionsResponse?.data || []
  ).map((company) => ({
    label: company.label,
    value: company.id.toString(),
  }));

  const fetchSourceOptions = useMemo<AxiosRequestConfig | null>(() => {
    return {
      method: "GET",
      url: `${baseUrl}/finance-export-source-list?dashboard_id=${dashboardId}`,
    };
  }, [dashboardId]);

  const { data: sourceOptionsResponse, loading: sourceOptionsLoading } =
    useFetch<{ data: FinanceExportSourceOption[] }>(fetchSourceOptions);

  const sourceOptions: FinanceExportSourceOption[] =
    sourceOptionsResponse?.data || [];

  const generateExport = async (requestData: FinanceExportRequestData) => {
    setLoading(true);
    try {
      const response = await axiosConfig({
        method: "POST",
        url: `${baseUrl}/finance-export`,
        data: requestData,
      });
      if (response.status === 201) {
        await fetchFinanceExportData({
          page_size: 50,
          page: 1,
        });
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error generating export:", error);
      setLoading(false);
      throw error;
    }
  };

  const handleExport = async (
    itemId: number,
    exportDataType: string,
    startDate: string,
    endDate: string
  ): Promise<void> => {
    setDownloadingItems((prev) => new Set(prev).add(itemId));

    const requestBody = {
      start_date: startDate,
      end_date: endDate,
    };

    try {
      const response = await axiosConfig({
        method: "POST",
        url: `${baseUrl}/finance-export/${itemId}/download?dashboard_id=${dashboardId}`,
        data: requestBody,
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: MIME_TYPES["excel"],
      });
      const link = document.createElement("a");
      link.href = globalThis.URL.createObjectURL(blob);

      link.setAttribute(
        "download",
        `FinanceExport-${exportDataType}-${startDate}-${endDate}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("There was an error downloading the file:", error);
      throw error;
    } finally {
      setDownloadingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  return {
    loading,
    error,
    tableData,
    totalCount,
    fetchFinanceExportData,
    typeOptions,
    typeOptionsLoading,
    companyOptions,
    companyOptionsLoading,
    sourceOptions,
    sourceOptionsLoading,
    generateExport,
    handleExport,
    downloadingItems,
  };
};

export default useFinanceExportData;
